import jsPDF from "jspdf";
import { BRAND } from "@/config/brand";

interface InvoiceItem {
  serviceId: string;
  name: string;
  description: string | null;
  unitPrice: number | string;
  quantity: number | string;
  total: number | string;
  type: string;
}

interface InvoiceData {
  invoice_number: string;
  invoice_date: string;
  amount: number | string;
  signature_url: string | null;
  patient: {
    name: string;
    email: string | null;
    phone: string | null;
    address: string | null;
    date_of_birth: string | null;
  };
  service?: {
    name: string;
    description: string | null;
    price: number | string;
  };
  items?: InvoiceItem[];
  practitioner: {
    business_name: string;
    practitioner_name: string;
    address: string;
    phone: string;
    email: string;
    siret: string | null;
  };
}

// REGARD DE NATURE color palette
const colors = {
  primary: [BRAND.colors.rgb.forestGreen.r, BRAND.colors.rgb.forestGreen.g, BRAND.colors.rgb.forestGreen.b],
  secondary: [BRAND.colors.rgb.softAccent.r, BRAND.colors.rgb.softAccent.g, BRAND.colors.rgb.softAccent.b],
  accent: [BRAND.colors.rgb.gold.r, BRAND.colors.rgb.gold.g, BRAND.colors.rgb.gold.b],
  background: [BRAND.colors.rgb.lightBackground.r, BRAND.colors.rgb.lightBackground.g, BRAND.colors.rgb.lightBackground.b],
  text: [BRAND.colors.rgb.text.r, BRAND.colors.rgb.text.g, BRAND.colors.rgb.text.b],
  lightGray: [220, 220, 220],
  white: [255, 255, 255],
};

// Logo URL public Supabase
const LOGO_URL = "https://hukmgamisjjgulkqacxm.supabase.co/storage/v1/object/public/assets/regard-de-nature-logo.png";

export const generateInvoicePDF = async (
  invoice: InvoiceData,
  options: { save?: boolean } = {}
): Promise<Uint8Array> => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    const num = (v: unknown, fallback = 0) => {
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    const addText = (
      text: string,
      x: number,
      y: number,
      opts: {
        size?: number;
        color?: number[];
        align?: "left" | "center" | "right";
        bold?: boolean;
      } = {}
    ) => {
      const { size = 10, color = colors.text, align = "left", bold = false } = opts;
      doc.setFontSize(size);
      doc.setTextColor(color[0], color[1], color[2]);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.text(text || "", x, y, { align });
    };

    // === HEADER ===
    doc.setFillColor(colors.background[0], colors.background[1], colors.background[2]);
    doc.rect(0, 0, pageWidth, 62, "F");

    doc.setFillColor(colors.accent[0], colors.accent[1], colors.accent[2]);
    doc.rect(0, 62, pageWidth, 2, "F");

    // Logo
    try {
      const res = await fetch(LOGO_URL);
      if (res.ok) {
        const blob = await res.blob();
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => resolve("");
          reader.readAsDataURL(blob);
        });
        if (base64) {
          doc.addImage(base64, "PNG", pageWidth / 2 - 14, 4, 28, 28);
        }
      }
    } catch (e) {
      console.warn("Logo non chargé:", e);
    }

    // Brand Name
    addText(BRAND.name.line1, pageWidth / 2, 40, { size: 18, color: colors.accent, align: "center", bold: true });
    addText(BRAND.name.line2, pageWidth / 2, 49, { size: 18, color: colors.primary, align: "center", bold: true });
    addText(BRAND.baseline.full, pageWidth / 2, 57, { size: 10, color: colors.text, align: "center" });

    yPos = 76;

    // === FACTURE TITLE ===
    addText("FACTURE", pageWidth / 2, yPos, { size: 20, color: colors.primary, align: "center", bold: true });
    yPos += 12;

    addText(`N° ${invoice.invoice_number}`, pageWidth / 2, yPos, { size: 11, color: colors.text, align: "center", bold: true });
    yPos += 6;

    const invoiceDate = new Date(invoice.invoice_date);
    addText(
      `Facturé le ${invoiceDate.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`,
      pageWidth / 2,
      yPos,
      { size: 10, color: colors.text, align: "center" }
    );
    yPos += 18;

    // === PRACTITIONER & PATIENT INFO ===
    const leftCol = 20;
    const rightCol = pageWidth / 2 + 10;
    const infoStartY = yPos;

    // Practitioner
    let practitionerY = infoStartY;
    doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    doc.rect(leftCol - 2, practitionerY - 5, 80, 8, "F");
    addText("PRATICIEN", leftCol, practitionerY, { size: 11, color: colors.white, bold: true });

    practitionerY += 10;
    addText(invoice.practitioner.business_name, leftCol, practitionerY, { size: 11, bold: true, color: colors.primary });
    practitionerY += 6;
    addText(invoice.practitioner.practitioner_name, leftCol, practitionerY, { size: 10 });
    practitionerY += 5;

    const practitionerLines = doc.splitTextToSize(invoice.practitioner.address, 80);
    practitionerLines.forEach((line: string) => {
      addText(line, leftCol, practitionerY, { size: 9 });
      practitionerY += 4;
    });

    practitionerY += 2;
    addText(invoice.practitioner.phone, leftCol, practitionerY, { size: 9 });
    practitionerY += 4;
    addText(invoice.practitioner.email, leftCol, practitionerY, { size: 9 });
    practitionerY += 4;

    if (invoice.practitioner.siret) {
      addText(`SIRET: ${invoice.practitioner.siret}`, leftCol, practitionerY, { size: 9 });
    }

    // Patient
    let patientY = infoStartY;
    doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    doc.rect(rightCol - 2, patientY - 5, 80, 8, "F");
    addText("CONSULTANT", rightCol, patientY, { size: 11, color: colors.white, bold: true });

    patientY += 10;
    addText(invoice.patient.name, rightCol, patientY, { size: 11, bold: true });
    patientY += 6;

    if (invoice.patient.address) {
      const addressLines = doc.splitTextToSize(invoice.patient.address, 80);
      addressLines.forEach((line: string) => {
        addText(line, rightCol, patientY, { size: 9 });
        patientY += 4;
      });
      patientY += 1;
    }

    if (invoice.patient.email) {
      addText(invoice.patient.email, rightCol, patientY, { size: 9 });
      patientY += 4;
    }

    if (invoice.patient.phone) {
      addText(invoice.patient.phone, rightCol, patientY, { size: 9 });
      patientY += 4;
    }

    if (invoice.patient.date_of_birth) {
      const birthDate = new Date(invoice.patient.date_of_birth);
      addText(`Né(e) le: ${birthDate.toLocaleDateString("fr-FR")}`, rightCol, patientY, { size: 9 });
    }

    yPos = Math.max(practitionerY, patientY) + 15;

    // === SERVICE DETAILS TABLE ===
    doc.setFillColor(colors.background[0], colors.background[1], colors.background[2]);
    doc.rect(leftCol - 2, yPos, pageWidth - 2 * leftCol + 4, 10, "F");

    addText("DÉSIGNATION", leftCol, yPos + 7, { size: 10, color: colors.text, bold: true });
    addText("QTÉ", pageWidth - 70, yPos + 7, { size: 10, color: colors.text, align: "center", bold: true });
    addText("P.U.", pageWidth - 45, yPos + 7, { size: 10, color: colors.text, align: "right", bold: true });
    addText("TOTAL", pageWidth - leftCol, yPos + 7, { size: 10, color: colors.text, align: "right", bold: true });

    yPos += 15;

    const items =
      invoice.items && invoice.items.length > 0
        ? invoice.items
        : invoice.service
          ? [{ serviceId: "", name: invoice.service.name, description: invoice.service.description, unitPrice: invoice.service.price, quantity: 1, total: invoice.service.price, type: "service" }]
          : [];

    const computedTotalAmount = items.reduce((sum, it) => sum + num(it.total), 0);
    const totalAmount = num(invoice.amount) > 0 ? num(invoice.amount) : computedTotalAmount;

    items.forEach((item, index) => {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      addText(item.name, leftCol, yPos, { size: 10, bold: true });
      addText(num(item.quantity, 1).toString(), pageWidth - 70, yPos, { size: 10, align: "center" });
      addText(`${num(item.unitPrice).toFixed(2)} €`, pageWidth - 45, yPos, { size: 10, align: "right" });
      addText(`${num(item.total).toFixed(2)} €`, pageWidth - leftCol, yPos, { size: 10, align: "right", bold: true });

      yPos += 5;

      if (item.description) {
        const descLines = doc.splitTextToSize(item.description, pageWidth - 120);
        descLines.forEach((line: string) => {
          addText(line, leftCol, yPos, { size: 8, color: [100, 100, 100] });
          yPos += 4;
        });
      }

      yPos += 3;

      if (index < items.length - 1) {
        doc.setDrawColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
        doc.setLineWidth(0.2);
        doc.line(leftCol, yPos, pageWidth - leftCol, yPos);
        yPos += 5;
      }
    });

    yPos += 5;
    doc.setDrawColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
    doc.setLineWidth(0.5);
    doc.line(leftCol, yPos, pageWidth - leftCol, yPos);

    yPos += 10;

    // === TOTAL ===
    doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    doc.rect(pageWidth - 90, yPos - 5, 70, 12, "F");

    addText("TOTAL TTC", pageWidth - 85, yPos + 3, { size: 12, color: colors.white, bold: true });
    addText(`${totalAmount.toFixed(2)} €`, pageWidth - leftCol, yPos + 3, { size: 12, color: colors.white, align: "right", bold: true });

    yPos += 25;

    // === TVA INFO ===
    doc.setFillColor(colors.background[0], colors.background[1], colors.background[2]);
    doc.rect(leftCol - 2, yPos - 3, pageWidth - 2 * leftCol + 4, 10, "F");
    addText("TVA non applicable - Article 293B du CGI", pageWidth / 2, yPos + 3, { size: 9, color: colors.text, align: "center" });

    yPos += 20;

    // === SIGNATURE ===
    if (invoice.signature_url) {
      try {
        addText("Signature:", pageWidth - 70, yPos, { size: 10, bold: true });
        yPos += 5;
        doc.addImage(invoice.signature_url, "PNG", pageWidth - 70, yPos, 50, 20);
        yPos += 25;
        addText(new Date(invoice.invoice_date).toLocaleDateString("fr-FR"), pageWidth - 70, yPos, { size: 9 });
      } catch (error) {
        console.error("Erreur ajout signature:", error);
      }
    }

    // === FOOTER ===
    const footerY = pageHeight - 25;

    doc.setDrawColor(colors.accent[0], colors.accent[1], colors.accent[2]);
    doc.setLineWidth(1);
    doc.line(20, footerY, pageWidth - 20, footerY);

    addText(`Merci de votre confiance - ${BRAND.name.full}, ${BRAND.baseline.full}`, pageWidth / 2, footerY + 8, { size: 9, color: colors.primary, align: "center" });
    addText(`${invoice.practitioner.email} - ${invoice.practitioner.phone}`, pageWidth / 2, footerY + 14, { size: 8, color: colors.text, align: "center" });

    // Save / return PDF
    const fileName = `Facture_${invoice.invoice_number}_${(invoice.patient?.name || "client").replace(/\s+/g, "_")}.pdf`;

    const buffer = doc.output("arraybuffer") as ArrayBuffer;
    const pdfBytes = new Uint8Array(buffer);

    if (options.save !== false) {
      doc.save(fileName);
    }

    return pdfBytes;
  } catch (error) {
    console.error("Erreur lors de la génération du PDF:", error);
    throw new Error("Erreur lors de la génération de la facture PDF");
  }
};
